import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Send } from 'lucide-react';
import ReactGA from 'react-ga4';

const Careers = () => {
    React.useEffect(() => {
        window.scrollTo(0, 0);
        try { ReactGA.send({ hitType: 'pageview', page: '/careers' }); } catch { }
    }, []);

    return (
        <div className="min-h-screen bg-[#050505] selection:bg-emerald/30 selection:text-emerald font-sans flex items-center justify-center">
            {/* Background Gradients */}
            <div className="fixed inset-0 z-0 pointer-events-none">
                <div className="absolute top-0 left-0 right-0 h-[500px] bg-gradient-to-b from-emerald/10 to-transparent" />
                <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-emerald/5 rounded-full blur-[120px]" />
            </div>

            {/* Content */}
            <div className="container mx-auto px-6 py-32 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-4xl mx-auto text-center"
                >
                    <motion.a
                        href="https://mail.google.com/mail/?view=cm&fs=1&to=hr@visist.ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        className="inline-block mb-8 px-6 py-2 rounded-full bg-emerald/10 border border-emerald/20 hover:bg-emerald/20 cursor-pointer hover:scale-105 transition-all duration-300"
                    >
                        <span className="text-emerald font-bold text-sm uppercase tracking-wider">Join Our Team</span>
                    </motion.a>

                    <h1 className="text-5xl md:text-7xl font-black text-white mb-8 leading-[1.1] tracking-tight uppercase">
                        Build the future of{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald via-neon-lime to-emerald">
                            Sports Intelligence
                        </span>
                    </h1>

                    <p className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed max-w-3xl mx-auto mb-12">
                        We're looking for passionate individuals to join our mission of empowering athletes through AI-powered insights.
                    </p>

                    {/* Contact Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                        className="max-w-2xl mx-auto bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-12 mb-8"
                    >
                        <div className="flex items-center justify-center gap-4 mb-6">
                            <div className="w-16 h-16 rounded-2xl bg-emerald/10 flex items-center justify-center">
                                <Mail className="w-8 h-8 text-emerald" />
                            </div>
                        </div>

                        <h2 className="text-3xl font-bold text-white mb-4">
                            Send Your Resume
                        </h2>

                        <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                            Interested in internships or full-time positions? Send your resume to:
                        </p>

                        <a
                            href="https://mail.google.com/mail/?view=cm&fs=1&to=hr@visist.ai"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 px-8 py-4 bg-emerald hover:bg-emerald-600 text-white font-bold text-xl rounded-full transition-all transform hover:scale-105 shadow-[0_0_30px_rgba(16,185,129,0.3)]"
                        >
                            <Send className="w-5 h-5" />
                            hr@visist.ai
                        </a>

                        <p className="text-gray-400 text-sm mt-8">
                            We'll review your application and reach out to you soon!
                        </p>
                    </motion.div>

                    <p className="text-gray-500 text-sm">
                        Looking forward to hearing from talented individuals who want to make an impact in sports technology.
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default Careers;

/* ============================================
   FULL CAREERS PAGE - COMMENTED OUT FOR V2
   ============================================

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Rocket, Users, Heart, Upload, Send, CheckCircle, AlertCircle } from 'lucide-react';
import ReactGA from 'react-ga4';

const Careers = () => {
    const [formData, setFormData] = useState({
        // Personal Information
        fullName: '',
        email: '',
        phone: '',
        location: '',
        linkedIn: '',
        portfolio: '',

        // Professional Background
        currentRole: '',
        yearsExperience: '',
        education: '',
        fieldOfStudy: '',
        skills: '',

        // Application Details
        position: '',
        resume: null,
        coverLetter: '',
        hearAboutUs: '',

        // Availability & Expectations
        noticePeriod: '',
        salaryExpectation: '',
        workMode: '',
        startDate: '',

        // Additional Questions
        sportsTechExperience: '',
        sportsInterest: '',
        technicalChallenge: '',
        whyVisist: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    React.useEffect(() => {
        window.scrollTo(0, 0);
        try { ReactGA.send({ hitType: 'pageview', page: '/careers' }); } catch { }

        // Enable smooth scrolling for anchor links
        document.documentElement.style.scrollBehavior = 'smooth';

        return () => {
            document.documentElement.style.scrollBehavior = '';
        };
    }, []);

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && (file.type === 'application/pdf' || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
            setFormData(prev => ({ ...prev, resume: file }));
        } else {
            alert('Please upload a PDF or DOC file');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate form submission
        setTimeout(() => {
            console.log('Form submitted:', formData);
            setSubmitStatus('success');
            setIsSubmitting(false);

            // Reset form after successful submission
            setTimeout(() => {
                setFormData({
                    fullName: '', email: '', phone: '', location: '', linkedIn: '', portfolio: '',
                    currentRole: '', yearsExperience: '', education: '', fieldOfStudy: '', skills: '',
                    position: '', resume: null, coverLetter: '', hearAboutUs: '',
                    noticePeriod: '', salaryExpectation: '', workMode: '', startDate: '',
                    sportsTechExperience: '', sportsInterest: '', technicalChallenge: '', whyVisist: ''
                });
                setSubmitStatus(null);
            }, 3000);
        }, 2000);
    };

    const openRoles = [
        {
            title: 'Senior AI/ML Engineer',
            department: 'Engineering',
            location: 'Bangalore / Remote',
            description: 'Build computer vision models for sports video analysis and real-time coaching insights.'
        },
        {
            title: 'Full Stack Developer',
            department: 'Engineering',
            location: 'Bangalore / Hybrid',
            description: 'Create seamless web and mobile experiences for coaches, players, and academies.'
        },
        {
            title: 'Sports Scientist',
            department: 'Product',
            location: 'Bangalore',
            description: 'Translate biomechanics and sports science into actionable AI-powered insights.'
        },
        {
            title: 'Product Designer',
            department: 'Design',
            location: 'Remote',
            description: 'Design intuitive interfaces that make complex AI insights accessible to everyone.'
        }
    ];

    const valueProps = [
        {
            icon: Rocket,
            title: 'Real Problems',
            description: 'Work at the intersection of AI, computer vision, and live sport—solving challenges that directly impact how athletes train and compete.'
        },
        {
            icon: Briefcase,
            title: 'Early-Stage Ownership',
            description: 'From research to on-court deployment, you\'ll own your work end-to-end and see its impact in real-time.'
        },
        {
            icon: Heart,
            title: 'Culture of Excellence',
            description: 'Join a team that values experimentation, continuous learning, and a genuine love for sport.'
        }
    ];

    return (
        <div className="min-h-screen bg-[#050505] selection:bg-emerald/30 selection:text-emerald font-sans">
            // ... REST OF THE FULL CAREERS PAGE CODE ...
        </div>
    );
};

export default Careers;

============================================ 
*/
